import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { subscriptionApi } from '../services/api';
import { Subscription } from '../types';

const SubscriptionBanner: React.FC = () => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await subscriptionApi.getUserSubscription();
      const subscriptionData = response.data;
      
      if (subscriptionData) {
        setSubscription(subscriptionData);
        
        // Show banner if subscription is expiring soon or expired
        if (subscriptionData.status === 'expiring_soon' || subscriptionData.status === 'expired') {
          setShowBanner(true);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowBanner(false);
  };


  if (loading || !subscription || !showBanner) {
    return null;
  }

  const getAlertProps = () => {
    switch (subscription.status) {
      case 'expiring_soon':
        return {
          severity: 'warning' as const,
          icon: <WarningIcon />,
          title: 'Subscription Expiring Soon',
          message: `Your subscription will expire in ${subscription.days_remaining} days. Please contact the support team to renew.`,
        };
      case 'expired':
        return {
          severity: 'error' as const,
          icon: <ErrorIcon />,
          title: 'Subscription Expired',
          message: 'Your subscription has expired. Please contact the support team to renew immediately.',
        };
      default:
        return null;
    }
  };

  const alertProps = getAlertProps();
  if (!alertProps) return null;

  return (
    <Collapse in={showBanner}>
      <Alert
        severity={alertProps.severity}
        icon={alertProps.icon}
        action={
          <IconButton
            size="small"
            onClick={handleClose}
            color="inherit"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ 
          mb: 2,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <AlertTitle>{alertProps.title}</AlertTitle>
        <Typography variant="body2">
          {alertProps.message}
        </Typography>
        {subscription.renew_date && (
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            Renewal Date: {new Date(subscription.renew_date).toLocaleDateString()}
          </Typography>
        )}
      </Alert>
    </Collapse>
  );
};

export default SubscriptionBanner;
